import { useState } from "react";
import "./SignalOverviewModal.scss";

function polarToCart(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

function arcStroke(cx, cy, r, startAngle, endAngle) {
  const p1 = polarToCart(cx, cy, r, startAngle);
  const p2 = polarToCart(cx, cy, r, endAngle);
  return `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} A ${r} ${r} 0 0 0 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
}

const getTargetPeriod = (validity) => {
  if (!validity) return "—";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(validity);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff <= 1) return "Intraday";
  if (diff <= 7) return "Swing";
  return "Quarterly";
};

const formatValidity = (dateStr) => {
  if (!dateStr) return "—";
  const [y, m, day] = dateStr.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`;
};

const SEGMENTS = [
  { start: 169, end: 161, color: "#c62828", minScore: 0    },
  { start: 139, end: 131, color: "#e64a19", minScore: 1.67 },
  { start: 109, end: 101, color: "#fb8c00", minScore: 3.33 },
  { start: 79,  end: 71,  color: "#fdd835", minScore: 5.0  },
  { start: 49,  end: 41,  color: "#8bc34a", minScore: 6.67 },
  { start: 19,  end: 11,  color: "#2e7d32", minScore: 8.33 },
];

export default function SignalOverviewModal({
  open, onClose,
  coinName, opinion, validity, leverage,
  entry, entryRange1, entryRange2,
  target1, target2, stoploss,
  score = 6,
  tags = [],
  description = "",
  attachmentCount = 0,
}) {
  const [publishTo, setPublishTo] = useState("All");

  if (!open) return null;

  const e  = Number(entry);
  const t1 = Number(target1);
  const t2 = Number(target2);
  const sl = Number(stoploss);
  const isLong = opinion === "long";

  const maxUpside = e
    ? isLong ? (((t2 - e) / e) * 100).toFixed(2) : (((e - t2) / e) * 100).toFixed(2)
    : null;
  const maxDownside = e
    ? isLong ? (((e - sl) / e) * 100).toFixed(2) : (((sl - e) / e) * 100).toFixed(2)
    : null;
  const risk = isLong ? e - sl : sl - e;
  const rr1 = risk > 0 ? ((isLong ? t1 - e : e - t1) / risk).toFixed(1) : null;
  const rr2 = risk > 0 ? ((isLong ? t2 - e : e - t2) / risk).toFixed(1) : null;

  // Gauge
  const cx = 110; const cy = 110; const r = 82;
  const needleAngle = (score / 10) * 180 - 90;
  const needleTip = polarToCart(cx, cy, r - 24, needleAngle);

  const entryDisplay = entryRange1 && entryRange2
    ? `₹ ${entryRange1} - ₹ ${entryRange2}`
    : entry ? `₹ ${entry}` : "—";

  return (
    <div className="sov-overlay" onClick={onClose}>
      <div className="sov-modal" onClick={(ev) => ev.stopPropagation()}>

        {/* HEADER */}
        <div className="sov-header">
          <button className="sov-back" onClick={onClose}>‹</button>
          <span className="sov-title">Signal Overview</span>
          <div className="sov-header-right" />
        </div>

        <div className="sov-body">

          {/* ── LEFT PANEL ── */}
          <div className="sov-left">

            <div className="sov-field sov-field--divider">
              <span className="sov-lbl">Coin Name</span>
              <span className="sov-val sov-val--xl">{coinName || "—"}</span>
            </div>

            <div className="sov-row sov-field--divider">
              <div className="sov-col">
                <span className="sov-lbl">My opinion</span>
                <span className={`sov-val sov-opinion ${isLong ? "long" : "short"}`}>
                  {isLong ? "Long" : "Short"}
                </span>
              </div>
              <div className="sov-col">
                <span className="sov-lbl">Target Period</span>
                <span className="sov-val">{getTargetPeriod(validity)}</span>
              </div>
            </div>

            <div className="sov-row sov-field--divider">
              <div className="sov-col">
                <span className="sov-lbl">Validity</span>
                <span className="sov-val">{formatValidity(validity)}</span>
              </div>
              <div className="sov-col">
                <span className="sov-lbl">Leverage</span>
                <span className="sov-val">{leverage ? `${leverage}x` : "—"}</span>
              </div>
            </div>

            <div className="sov-field sov-field--divider">
              <span className="sov-lbl">Tags</span>
              <span className="sov-val">
                {tags.length > 0 ? tags.join(", ") : "—"}
              </span>
            </div>

            <div className="sov-field sov-field--divider">
              <span className="sov-lbl">Uploads</span>
              <span className="sov-val">
                {attachmentCount > 0 ? `Attachment - ${attachmentCount}` : "—"}
              </span>
            </div>

            <div className="sov-field">
              <span className="sov-lbl">Logic</span>
              <p className="sov-logic-text">
                {description || "—"}
              </p>
            </div>

          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="sov-right">

            {/* Gauge + Score */}
            <div className="sov-gauge-row">
              <svg viewBox="0 0 220 120" className="sov-gauge-svg">
                {SEGMENTS.map((seg, i) => (
                  <path
                    key={i}
                    d={arcStroke(cx, cy, r, seg.start, seg.end)}
                    fill="none"
                    stroke={score > seg.minScore ? seg.color : "#252b45"}
                    strokeWidth={26}
                    strokeLinecap="round"
                  />
                ))}
                <line
                  x1={cx} y1={cy}
                  x2={needleTip.x.toFixed(2)} y2={needleTip.y.toFixed(2)}
                  stroke="#fff" strokeWidth="2.5" strokeLinecap="round"
                />
                <circle cx={cx} cy={cy} r="5" fill="#1b2236" stroke="#fff" strokeWidth="2" />
                <text x="8"   y={cy + 8} fill="#e53935" fontSize="11" fontWeight="700">Low</text>
                <text x="175" y={cy + 8} fill="#2e7d32" fontSize="11" fontWeight="700">High</text>
              </svg>
              <div className="sov-score-info">
                <span className="sov-score-num">{String(score).padStart(2, "0")}</span>
                <span className="sov-score-lbl">AI Confidence Score</span>
              </div>
            </div>

            <div className="sov-divider" />

            {/* Entry / Targets / Stoploss */}
            <div className="sov-stats">
              <div className="sov-stat">
                <span className="sov-stat-lbl">Entry Range</span>
                <span className="sov-stat-val">{entryDisplay}</span>
              </div>
              <div className="sov-stat">
                <span className="sov-stat-lbl">Target 1</span>
                <span className="sov-stat-val">{target1 ? `₹ ${target1}` : "—"}</span>
              </div>
              <div className="sov-stat">
                <span className="sov-stat-lbl">Target 2</span>
                <span className="sov-stat-val">{target2 ? `₹ ${target2}` : "—"}</span>
              </div>
              <div className="sov-stat sov-stat--last">
                <span className="sov-stat-lbl">Stoploss</span>
                <span className="sov-stat-val">{stoploss ? `₹ ${stoploss}` : "—"}</span>
              </div>
            </div>

            <div className="sov-divider" />

            {/* Max Upside / Downside */}
            <div className="sov-updown-row">
              <div className="sov-updown sov-updown--up">
                <span className="sov-updown-lbl">Max Upside</span>
                <span className="sov-updown-val up">{maxUpside !== null ? `${maxUpside}%` : "—"}</span>
              </div>
              <div className="sov-updown sov-updown--down">
                <span className="sov-updown-lbl">Max Downside</span>
                <span className="sov-updown-val down">{maxDownside !== null ? `${maxDownside}%` : "—"}</span>
              </div>
            </div>

            <div className="sov-divider" />

            {/* RR Ratios */}
            <div className="sov-rr-row">
              <span className="sov-rr-item">
                <span className="sov-rr-lbl">Max RR Ratio 1 </span>
                <span className="sov-rr-val">{rr1 !== null ? `${rr1} : 1` : "—"}</span>
              </span>
              <span className="sov-rr-item">
                <span className="sov-rr-lbl">Max RR Ratio 2 </span>
                <span className="sov-rr-val">{rr2 !== null ? `${rr2} : 1` : "—"}</span>
              </span>
            </div>

            <div className="sov-divider" />

            {/* Publish */}
            <div className="sov-publish-row">
              <div className="sov-publish-left">
                <span className="sov-publish-lbl">Publish To</span>
                <div className="sov-select-wrap">
                  <select
                    className="sov-select"
                    value={publishTo}
                    onChange={(ev) => setPublishTo(ev.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Premium">Premium</option>
                  </select>
                  <span className="sov-select-arrow">▾</span>
                </div>
              </div>
              <div className="sov-publish-btns">
                <button className="sov-btn sov-btn--private">Private</button>
                <button className="sov-btn sov-btn--public">Public</button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
